(() => {
  'use strict';

  if (customElements.get('delivery-cutoff')) {
    return;
  }

  const DEFAULT_TIME_ZONE = 'Europe/Warsaw';
  const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  class DeliveryCutoff extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-delivery-cutoff-output]');

      if (!this.output) {
        return;
      }

      this.cutoffHour = this.parseNumber(this.dataset.cutoffHour, 13);
      this.cutoffMinute = this.parseNumber(this.dataset.cutoffMinute, 0);
      this.cutoffTime = this.dataset.cutoffTime || `${this.pad(this.cutoffHour)}:${this.pad(this.cutoffMinute)}`;
      this.showSeconds = this.dataset.showSeconds === 'true';
      this.beforeTemplate = this.getTemplate('before');
      this.afterTemplate = this.getTemplate('after');
      this.labels = this.getLabels();
      this.units = this.getUnits();
      this.timeZone = this.dataset.timeZone || DEFAULT_TIME_ZONE;

      try {
        this.formatter = this.createFormatter(this.timeZone);
        this.getZonedParts(new Date());
      } catch (error) {
        this.timeZone = DEFAULT_TIME_ZONE;
        this.formatter = this.createFormatter(this.timeZone);
      }

      this.update();
      this.interval = window.setInterval(() => this.update(), this.showSeconds ? 1000 : 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
    }

    parseNumber(value, fallback) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? fallback : parsed;
    }

    pad(value) {
      return String(value).padStart(2, '0');
    }

    createFormatter(timeZone) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    }

    getTemplate(state) {
      const template = this.querySelector(`[data-delivery-cutoff-template="${state}"]`);
      return template ? template.innerHTML.trim() : '';
    }

    getLabels() {
      return WEEKDAY_KEYS.reduce(
        (labels, key) => {
          labels[key] = this.dataset[`label${this.toDatasetKey(key)}`] || key;
          return labels;
        },
        {
          today: this.dataset.labelToday || 'today',
          tomorrow: this.dataset.labelTomorrow || 'tomorrow',
        }
      );
    }

    getUnits() {
      return {
        day: this.dataset.unitDay || 'd',
        hour: this.dataset.unitHour || 'h',
        minute: this.dataset.unitMinute || 'min',
        second: this.dataset.unitSecond || 's',
      };
    }

    toDatasetKey(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    getZonedParts(date) {
      const parts = this.formatter.formatToParts(date).reduce((dateParts, part) => {
        if (part.type !== 'literal') {
          dateParts[part.type] = part.value;
        }

        return dateParts;
      }, {});

      return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
        second: Number(parts.second),
      };
    }

    update() {
      const schedule = this.getSchedule(new Date());
      const template = schedule.beforeCutoff ? this.beforeTemplate : this.afterTemplate;

      this.output.innerHTML = this.renderTemplate(template, schedule);
    }

    getSchedule(now) {
      const nowParts = this.getZonedParts(now);
      const today = this.getDateOnly(nowParts);
      const currentMinutes = nowParts.hour * 60 + nowParts.minute + nowParts.second / 60;
      const cutoffMinutes = this.cutoffHour * 60 + this.cutoffMinute;
      const beforeCutoff = this.isBusinessDay(today) && currentMinutes < cutoffMinutes;
      const shippingDate = beforeCutoff ? today : this.nextBusinessDay(today);
      const deliveryDate = this.nextBusinessDay(shippingDate);
      const cutoffTimestamp = this.zonedTimeToUtc(shippingDate, this.cutoffHour, this.cutoffMinute, 0);

      return {
        beforeCutoff,
        time: this.formatDuration(cutoffTimestamp - now.getTime()),
        cutoffTime: this.cutoffTime,
        shippingDay: this.getRelativeDayLabel(shippingDate, today),
        deliveryDay: this.getRelativeDayLabel(deliveryDate, today),
      };
    }

    getDateOnly(parts) {
      return {
        year: parts.year,
        month: parts.month,
        day: parts.day,
      };
    }

    zonedTimeToUtc(dateParts, hour, minute, second) {
      const targetAsUtc = Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day, hour, minute, second);
      let guess = targetAsUtc;

      for (let index = 0; index < 4; index += 1) {
        const parts = this.getZonedParts(new Date(guess));
        const currentAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        const difference = currentAsUtc - targetAsUtc;

        if (difference === 0) {
          break;
        }

        guess -= difference;
      }

      return guess;
    }

    nextBusinessDay(dateParts) {
      let nextDate = this.addDays(dateParts, 1);

      while (!this.isBusinessDay(nextDate)) {
        nextDate = this.addDays(nextDate, 1);
      }

      return nextDate;
    }

    addDays(dateParts, days) {
      const date = new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day + days));

      return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate(),
      };
    }

    isBusinessDay(dateParts) {
      const weekday = this.getWeekday(dateParts);
      return weekday >= 1 && weekday <= 5;
    }

    getWeekday(dateParts) {
      return new Date(Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day)).getUTCDay();
    }

    getRelativeDayLabel(dateParts, today) {
      if (this.isSameDate(dateParts, today)) {
        return this.labels.today;
      }

      if (this.isSameDate(dateParts, this.addDays(today, 1))) {
        return this.labels.tomorrow;
      }

      return this.labels[WEEKDAY_KEYS[this.getWeekday(dateParts)]];
    }

    isSameDate(firstDate, secondDate) {
      return (
        firstDate.year === secondDate.year &&
        firstDate.month === secondDate.month &&
        firstDate.day === secondDate.day
      );
    }

    formatDuration(milliseconds) {
      let totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
      const days = Math.floor(totalSeconds / 86400);
      totalSeconds %= 86400;
      const hours = Math.floor(totalSeconds / 3600);
      totalSeconds %= 3600;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const parts = [];

      if (days > 0) {
        parts.push(`${days}${this.units.day}`);
      }

      if (hours > 0 || days > 0) {
        parts.push(`${hours}${this.units.hour}`);
      }

      if (minutes > 0 || !this.showSeconds || parts.length === 0) {
        parts.push(`${minutes}${this.units.minute}`);
      }

      if (this.showSeconds) {
        parts.push(`${seconds}${this.units.second}`);
      }

      return parts.join(' ');
    }

    renderTemplate(template, schedule) {
      const replacements = {
        time: this.highlight(schedule.time),
        cutoff_time: this.highlight(schedule.cutoffTime),
        shipping_day: this.highlight(schedule.shippingDay),
        delivery_day: this.highlight(schedule.deliveryDay),
      };

      return template.replace(/\[\[(time|cutoff_time|shipping_day|delivery_day)\]\]/g, (match, token) => replacements[token]);
    }

    highlight(value) {
      return `<span class="product-delivery-cutoff__highlight">${this.escapeHtml(value)}</span>`;
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = value;
      return element.innerHTML;
    }
  }

  customElements.define('delivery-cutoff', DeliveryCutoff);
})();
